import { Send, MailCheck, MailOpen, MousePointerClick, AlertTriangle } from "lucide-react";

/**
 * Compact email-tracking pill that reflects the latest Resend delivery event.
 *
 * Props:
 *  - record: { status, delivered_at, opened_at, clicked_at, bounced_at, created_at, to } | null
 *
 * Renders nothing if no record exists (i.e., email hasn't been sent).
 */
export default function EmailStatusBadge({ record }) {
  if (!record) return null;
  // Order matters — clicked beats opened beats delivered beats sent.
  const tier = record.clicked_at ? "clicked"
    : record.opened_at ? "opened"
    : record.bounced_at ? "bounced"
    : record.delivered_at ? "delivered"
    : (record.status === "failed" ? "failed"
       : record.status === "bounced" ? "bounced"
       : "sent");

  const cfg = {
    sent:      { Icon: Send,              label: "Sent",      cls: "bg-slate-100 text-slate-600" },
    delivered: { Icon: MailCheck,         label: "Delivered", cls: "bg-blue-100 text-blue-700" },
    opened:    { Icon: MailOpen,          label: "Opened",    cls: "bg-emerald-100 text-emerald-700" },
    clicked:   { Icon: MousePointerClick, label: "Clicked",   cls: "bg-violet-100 text-violet-700" },
    bounced:   { Icon: AlertTriangle,     label: "Bounced",   cls: "bg-red-100 text-red-700" },
    failed:    { Icon: AlertTriangle,     label: "Failed",    cls: "bg-red-100 text-red-700" },
  }[tier];

  // Build a multi-line tooltip with timestamps
  const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : null;
  const lines = [
    record.to && `To: ${record.to}`,
    record.created_at && `Sent: ${fmt(record.created_at)}`,
    record.delivered_at && `Delivered: ${fmt(record.delivered_at)}`,
    record.opened_at && `Opened: ${fmt(record.opened_at)}`,
    record.clicked_at && `Clicked: ${fmt(record.clicked_at)}`,
    record.bounced_at && `Bounced: ${fmt(record.bounced_at)}`,
  ].filter(Boolean).join("\n");

  const Icon = cfg.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${cfg.cls}`}
      title={lines}
      data-testid="email-status-badge"
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}
